import { useState, type ComponentProps, type MouseEvent } from "react"
import { faCircleDollar, faHourglass, faThumbsUp, faTruckUtensils, faCupTogo } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"

import {
    MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE,
    MERCHANT_PRODUCTION_TAKEAWAY_ROUTE,
} from "../../../constants/routes"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { MerchantHomeQuery, fetchDashboardNotifications } from "../../../modules/merchant/merchant/home/merchantHomeApi"
import { OrderStatusType } from "../../../modules/merchant/merchant/home/merchantHomeTypes"
import {
    DashboardNotifications,
    TimePeriodMeetingService,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import { getOrdersByType } from "../../../modules/merchant/merchant/production/pages/MerchantProductionMeetingService"
import { useMerchant } from "../../../modules/merchants/merchantHooks"

type StyleProps = {
    isLargeAmount?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { isLargeAmount } = {}) => ({
    button: {
        color: theme.palette.tailwind.neutral[500],
        position: "relative",
    },
    counterBox: {
        background: "#2196F3",
        borderRadius: "6.25rem",
        color: theme.palette.common.white,
        padding: "0rem 0.406rem",
        fontSize: "0.7rem",
        lineHeight: "1rem",
        letterSpacing: "0.009rem",
        textAlign: "center",
        fontWeight: 500,
    },
    roundedCounterBox: {
        borderRadius: "50%",
        minWidth: "1.25rem",
        height: "1.25rem",
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    counter: {
        position: "absolute",
        top: "-0.25rem",
        right: "-0.25rem",
    },
    popoverContainer: {
        padding: "2rem",
        gap: "0.5rem",
    },
    twoColumnGrid: {
        gap: "2rem",
    },
    column: {
        gap: "0.5rem",
        minWidth: "20rem",
    },
    statusListItem: {
        padding: "0.75rem",
        flexDirection: "row",
        alignItems: "start",
        gap: "0.625rem",
        minWidth: "20rem",
        maxWidth: "20rem",
        cursor: "pointer",
    },
    statusItemIconContainer: {
        position: "relative",
        background: theme.palette.tailwind.amber[50],
        color: theme.palette.tailwind.amber[700],
        border: `1px solid ${theme.palette.tailwind.amber[300]}`,
        borderRadius: "0.5rem",
        minHeight: "2.5rem",
        minWidth: "2.5rem",
        justifyContent: "center",
        alignItems: "center",
    },
    statusItemIconContainerSlate: {
        color: theme.palette.tailwind.slate[700],
        background: theme.palette.tailwind.slate[50],
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    statusItemIconContainerGreen: {
        color: theme.palette.tailwind.green[700],
        background: theme.palette.tailwind.green[50],
        border: `1px solid ${theme.palette.tailwind.green[300]}`,
    },
    statusItemTitle: {
        fontWeight: 600,
        lineHeight: 1,
    },
    rightColumn: {
        gap: "0.375rem",
    },
    sectionHeader: {
        color: theme.palette.text.secondary,
        paddingTop: "1.25rem",
        paddingLeft: "0.75rem",
        paddingRight: "0.75rem",
        "&:first-of-type": {
            paddingTop: 0,
        },
    },
    columnSectionHeader: {
        color: theme.palette.text.secondary,
        paddingTop: 0,
        paddingLeft: "0.75rem",
        paddingRight: "0.75rem",
    },
}))

type SectionHeaderProps = Readonly<{
    title: string
    className: string
}>

function SectionHeader({ title, className }: SectionHeaderProps) {
    return (
        <Typography variant="subtitle2" className={className}>
            {title}
        </Typography>
    )
}

type PopoverStatusItemProps = Readonly<{
    className: string
    counterBoxClassName: string
    icon: ComponentProps<typeof FontAwesomeIcon>["icon"]
    iconContainerClassName: string
    iconContainerExtraClassName?: string
    onClick: () => void
    rightColumnClassName: string
    title: string
    titleClassName: string
    count: number
    description: string
}>

function PopoverStatusItem({
    className,
    counterBoxClassName,
    icon,
    iconContainerClassName,
    iconContainerExtraClassName,
    onClick,
    rightColumnClassName,
    title,
    titleClassName,
    count,
    description,
}: PopoverStatusItemProps) {
    return (
        <Stack className={className} onClick={onClick}>
            <Stack
                className={
                    iconContainerExtraClassName
                        ? `${iconContainerClassName} ${iconContainerExtraClassName}`
                        : iconContainerClassName
                }
            >
                <FontAwesomeIcon icon={icon} />
            </Stack>
            <Stack className={rightColumnClassName}>
                <Stack direction="row" gap={1}>
                    <Typography className={titleClassName}>{title}</Typography>
                    <Box className={counterBoxClassName}>{count}</Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Stack>
        </Stack>
    )
}

export function AwaitingApprovalNavItem() {
    const { merchantId } = useMerchant()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const navigate = useNavigate()

    const dashboardNotifications = useApiQuery(fetchDashboardNotifications, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS,
        dependencies: {
            merchantId,
            params: { status: OrderStatusType.READY_FOR_INVOICE.toUpperCase() },
        },
    })

    const toBeBookedAmount = getOrdersByType(
        DashboardNotifications.TO_BE_BOOKED,
        dashboardNotifications.data?.notifications,
    )
    const awaitingForApprovalAmount = getOrdersByType(
        DashboardNotifications.NOT_APPROVED,
        dashboardNotifications.data?.notifications,
    )
    const todaysMeetingsService = getOrdersByType(
        DashboardNotifications.TODAYS_MEETING_SERVICE_ORDERS,
        dashboardNotifications.data?.notifications,
    )
    const todaysTakeawayOrders = getOrdersByType(
        DashboardNotifications.TODAYS_TAKE_AWAY_ORDERS,
        dashboardNotifications.data?.notifications,
    )

    const totalAmount = awaitingForApprovalAmount + toBeBookedAmount + todaysTakeawayOrders + todaysMeetingsService
    const isLargeAmount = totalAmount > 99
    const open = Boolean(anchorEl)
    const id = open ? "simple-popover" : undefined
    const { classes, cx } = useStyles({ isLargeAmount })

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handleItemClick =
        (tabId: number = 0, period?: TimePeriodMeetingService) =>
        () => {
            handleClose()
            const periodParam = period ? `&period=${period}` : ""
            navigate(`${MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE}?tab=${tabId}${periodParam}`)
        }

    const handleTakeawayClick = () => {
        handleClose()
        navigate(`${MERCHANT_PRODUCTION_TAKEAWAY_ROUTE}?period=${TimePeriodMeetingService.TODAY}`)
    }

    return (
        <Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Stack
                    data-tour="merchant-product-tour-order-notifications-content"
                    className={classes.popoverContainer}
                >
                    <Grid container className={classes.twoColumnGrid} wrap="nowrap">
                        <Grid item xs={6}>
                            <Stack className={classes.column}>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-section-meeting-service-title"
                                    attrs={{ title: true }}
                                >
                                    <SectionHeader title="Meeting service" className={classes.columnSectionHeader} />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-todays-orders-item"
                                    attrs={{ title: true, description: true }}
                                >
                                    <PopoverStatusItem
                                        className={classes.statusListItem}
                                        counterBoxClassName={classes.counterBox}
                                        icon={faThumbsUp}
                                        iconContainerClassName={classes.statusItemIconContainer}
                                        iconContainerExtraClassName={classes.statusItemIconContainerGreen}
                                        onClick={handleItemClick(0, TimePeriodMeetingService.TODAY)}
                                        rightColumnClassName={classes.rightColumn}
                                        title="Today's orders"
                                        titleClassName={classes.statusItemTitle}
                                        count={todaysMeetingsService}
                                        description="Meeting service orders for delivery today"
                                    />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-not-approved-item"
                                    attrs={{ title: true, description: true }}
                                >
                                    <PopoverStatusItem
                                        className={classes.statusListItem}
                                        counterBoxClassName={classes.counterBox}
                                        icon={faHourglass}
                                        iconContainerClassName={classes.statusItemIconContainer}
                                        onClick={handleItemClick(1)}
                                        rightColumnClassName={classes.rightColumn}
                                        title="Awaiting approval"
                                        titleClassName={classes.statusItemTitle}
                                        count={awaitingForApprovalAmount}
                                        description="Orders that need to be approved"
                                    />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-to-be-booked-item"
                                    attrs={{ title: true, description: true }}
                                >
                                    <PopoverStatusItem
                                        className={classes.statusListItem}
                                        counterBoxClassName={classes.counterBox}
                                        icon={faCircleDollar}
                                        iconContainerClassName={classes.statusItemIconContainer}
                                        iconContainerExtraClassName={classes.statusItemIconContainerSlate}
                                        onClick={handleItemClick(2)}
                                        rightColumnClassName={classes.rightColumn}
                                        title="To be invoiced"
                                        titleClassName={classes.statusItemTitle}
                                        count={toBeBookedAmount}
                                        description="Meeting service orders that have been bookable for 3+ days"
                                    />
                                </LocalizedStrict>
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack className={classes.column}>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-section-takeaway-title"
                                    attrs={{ title: true }}
                                >
                                    <SectionHeader title="Takeaway" className={classes.columnSectionHeader} />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="awaiting-approval-popover-todays-takeaway-orders-item"
                                    attrs={{ title: true, description: true }}
                                >
                                    <PopoverStatusItem
                                        className={classes.statusListItem}
                                        counterBoxClassName={classes.counterBox}
                                        icon={faTruckUtensils}
                                        iconContainerClassName={classes.statusItemIconContainer}
                                        iconContainerExtraClassName={classes.statusItemIconContainerGreen}
                                        onClick={handleTakeawayClick}
                                        rightColumnClassName={classes.rightColumn}
                                        title="Today's orders"
                                        titleClassName={classes.statusItemTitle}
                                        count={todaysTakeawayOrders}
                                        description="Takeaway orders for today"
                                    />
                                </LocalizedStrict>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Popover>

            <LocalizedStrict id="awaiting-approval-popover-tooltip" attrs={{ title: true }}>
                <Tooltip title="Order notifications">
                    <IconButton
                        data-tour="merchant-product-tour-order-notifications"
                        size="medium"
                        onClick={handleClick}
                        className={classes.button}
                    >
                        <FontAwesomeIcon icon={faCupTogo} />
                        <Grid className={cx(classes.counter, classes.counterBox, classes.roundedCounterBox)}>
                            {isLargeAmount ? "99" : totalAmount || 0}
                        </Grid>
                    </IconButton>
                </Tooltip>
            </LocalizedStrict>
        </Box>
    )
}
